// React
import React, { useState, useContext } from "react";

// Components
import { Grid } from "../../../../components/Grid";
import { Typography } from "../../../../components/Typography";
import { PrimaryButton } from "../../../../components/Button";
import { Icon } from "../../../../components";
import {
  DropdownContainer,
  SelectedCount,
} from "./MoveSelectedApplications.styled";

// External imports
import ReactDropdown from "react-dropdown";

// State management
import { DispatchErrorContext } from "../../../../context/ErrorContext";
import { DispatchMessageContext } from "../../../../context/MessageContext";
import {
  useKanbanState,
  useKanbanDispatch,
} from "../../../context/KanbanContext";

// Translations
import { Trans, useTranslation } from "react-i18next";

const MoveSelectedApplications = ({ onClose }) => {
  const { t } = useTranslation();

  const STATUS_OPTIONS = [
    {
      value: "applied",
      label: t("component.application_status_list.applied", "Applied"),
    },
    {
      value: "shortlisted",
      label: t("component.application_status_list.shortlist", "Shortlisted"),
    },
    {
      value: "interview",
      label: t("component.application_status_list.interview", "Interview"),
    },
    {
      value: "personal_rejection",
      label: t(
        "component.application_status_list.personal_rejection",
        "Personal rejection",
      ),
    },
    {
      value: "hired",
      label: t("component.application_status_list.hired", "Hired"),
    },
    {
      value: "to_be_rejected",
      label: t(
        "component.application_status_list.to_be_rejected",
        "To be rejected",
      ),
    },
  ];

  const errorDispatch = useContext(DispatchErrorContext);
  const messageDispatch = useContext(DispatchMessageContext);

  const { selectedIds } = useKanbanState();
  const { updateCompactApplication, clearSelection } = useKanbanDispatch();

  const [selectedStatus, setSelectedStatus] = useState(STATUS_OPTIONS[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (option) => {
    setSelectedStatus(option);
  };

  const handleSubmit = async () => {
    if (!selectedStatus) return;

    setIsSubmitting(true);
    try {
      const ids = Array.from(selectedIds);
      const results = await Promise.all(
        ids.map((applicationId) =>
          updateCompactApplication({
            applicationId,
            substatus: selectedStatus.value,
          }),
        ),
      );

      const allSuccessful = results.every((r) => r?.success);
      if (allSuccessful) {
        const number_of_applications = ids.length;
        const status_destination = selectedStatus.label;
        clearSelection();
        messageDispatch({
          type: "set_message",
          payload: (
            <Trans
              i18nKey="message.applications_moved"
              number_of_applications={number_of_applications}
              status_destination={status_destination}
            >
              {{ number_of_applications }} application(s) moved to{" "}
              {{ status_destination }}
            </Trans>
          ),
        });
        // Reset state before closing so the component is still mounted
        setIsSubmitting(false);
        onClose();
      } else {
        setIsSubmitting(false);
        errorDispatch({
          type: "set_error",
          payload: t(
            "error.could_not_move_applications",
            "Some applications could not be moved.",
          ),
        });
      }
    } catch (err) {
      setIsSubmitting(false);
      errorDispatch({
        type: "set_error",
        payload: JSON.stringify(err),
      });
    }
  };

  return (
    <div>
      <Grid padding={32} fluid>
        <Typography tag="p" center>
          {t(
            "common.moveSelectedApplicationsDescription",
            "Select a destination status for the selected applications.",
          )}
        </Typography>
        <DropdownContainer>
          <SelectedCount>
            {selectedIds.size}{" "}
            {t("common.applicationsSelected", "application(s) selected")}
          </SelectedCount>
          <ReactDropdown
            options={STATUS_OPTIONS}
            onChange={handleChange}
            value={selectedStatus}
          />
          <PrimaryButton
            red
            onClick={handleSubmit}
            disabled={isSubmitting || !selectedStatus}
          >
            {t("common.moveSelected", "Move selected")}
            <Icon icon="arrow_u_turn_right_down" mr="10" />
          </PrimaryButton>
          <PrimaryButton transparent mt="18" onClick={onClose}>
            {t("common.cancel")}
            <Icon icon="cross" mr="10" invert />
          </PrimaryButton>
        </DropdownContainer>
      </Grid>
    </div>
  );
};

export default MoveSelectedApplications;
